import { useState, useEffect, useCallback, useRef } from 'react';
import { useTelegramContext } from './telegramContext';
import { apiService, UserResponse } from '../services/api_url';
import { useAppStore } from '../store/useAppStore';

interface UseUserDataReturn {
    userData: UserResponse | null;
    telegramId: string | null;
    userName: string;
    userPhoto: string | null;
    loading: boolean;
    error: string | null;
    isFromTelegram: boolean;
    refreshUserData: () => Promise<void>;
}

export function useUserData(): UseUserDataReturn {
    const { user, isTelegram, isReady } = useTelegramContext();
    const storeUserData = useAppStore((state) => state.userData);
    const storeTelegramId = useAppStore((state) => state.telegramId);
    const storeIsAuthenticated = useAppStore((state) => state.isAuthenticated);
    const storeIsUserLoading = useAppStore((state) => state.isUserLoading);
    const { loadUserData, setIsAuthenticated, setTelegramId, setUserData } = useAppStore();

    const [userName, setUserName] = useState<string>('');
    const [userPhoto, setUserPhoto] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isFromTelegram, setIsFromTelegram] = useState<boolean>(false);

    const loadingRef = useRef(false);
    const lastLoadRef = useRef<string>('');

    useEffect(() => {
        if (storeUserData) {
            setUserName(storeUserData.username || 'Пользователь');
        }
    }, [storeUserData]);

    const loadData = useCallback(async () => {
        if (!isReady || loadingRef.current) return;

        loadingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            if (isTelegram && user) {
                setIsFromTelegram(true);
                const tgId = user.id.toString();
                const displayName = user.username || `${user.first_name} ${user.last_name || ''}`.trim();

                setUserName(displayName || 'Пользователь');
                setUserPhoto(user.photo_url || null);
                setTelegramId(tgId);
                setIsAuthenticated(true);

                // Всегда загружаем свежие данные при монтировании
                lastLoadRef.current = tgId;
                await loadUserData(tgId);
                return;
            }

            setIsFromTelegram(false);
            setUserPhoto(null);

            if (storeIsAuthenticated && storeTelegramId && storeUserData) {
                setUserName(storeUserData.username || 'Пользователь');
                return;
            }

            const authCheck = await apiService.checkAuth();

            if (authCheck.status === 'authenticated') {
                const tgId = authCheck.user.telegram_id;
                setTelegramId(tgId);
                setIsAuthenticated(true);

                // Всегда загружаем свежие данные при монтировании
                lastLoadRef.current = tgId;
                const freshUser = await loadUserData(tgId);
                setUserName(freshUser?.username || 'Пользователь');
                return;
            }

            if (authCheck.status === 'unauthorized') {
                setIsAuthenticated(false);
                setUserData(null);
                setUserName('');
                setError('Не авторизован');
                return;
            }

            setError('Не удалось проверить авторизацию');
        } catch (err) {
            setError('Не удалось загрузить данные пользователя');
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [
        isReady,
        isTelegram,
        user,
        storeIsAuthenticated,
        storeTelegramId,
        storeUserData,
        loadUserData,
        setTelegramId,
        setIsAuthenticated,
        setUserData,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const refreshUserData = useCallback(async () => {
        const currentId = storeTelegramId;
        if (!currentId) return;

        setLoading(true);
        try {
            const freshUser = await loadUserData(currentId);
            setUserName(freshUser?.username || 'Пользователь');
            lastLoadRef.current = currentId;
        } finally {
            setLoading(false);
        }
    }, [storeTelegramId, loadUserData]);

    return {
        userData: storeUserData,
        telegramId: storeTelegramId,
        userName,
        userPhoto,
        loading: loading || storeIsUserLoading,
        error,
        isFromTelegram,
        refreshUserData,
    };
}